import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    IconButton,
    InputAdornment,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import { alpha } from '@mui/material/styles';
import { useFormik } from 'formik';
import { Link, useSearchParams } from 'react-router-dom';
import Logo from '../../../assets/logo.png';
import DocumentInputMasked from '../../../components/DocumentInputMasked';
import useErrors from '../../../hooks/useErrors';
import server from '../../../services/server';
import { useAuth } from '../../../context/AuthContext';

const HERO_IMAGE =
    'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=2000&q=80';

export default function AdvertiserLogin() {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const [params] = useSearchParams();
    const prefilledDocument = params.get('cnpj') || '';
    const { setErrors } = useErrors();
    const { Login } = useAuth();

    const { values, handleChange, handleSubmit, setFieldValue, isSubmitting } = useFormik({
        initialValues: {
            cnpj: prefilledDocument,
            email: '',
            password: '',
            showPassword: false,
        },
        enableReinitialize: true,
        onSubmit: async (formValues, { setSubmitting }) => {
            try {
                setSubmitting(true);
                const username = String(formValues.cnpj || '').replace(/\D/g, '');
                const emailTrim = formValues.email.trim().toLowerCase();
                const payload = {
                    username,
                    password: formValues.password,
                    profile: 'advertiser',
                };
                if (emailTrim) {
                    payload.email = emailTrim;
                }
                const { data } = await server.post('/auth', payload);
                const user = data.data.user;
                const advertiser = data.data.user.advertiser;
                await Login({
                    token: data.data.token,
                    refreshtoken: data.data.refreshToken,
                    user,
                    advertiser,
                });
            } catch (error) {
                setErrors(error);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const overlayGradient = `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.85)} 0%, ${alpha('#0f172a', 0.78)} 50%, ${alpha('#020617', 0.88)} 100%)`;

    const heroPanel = (
        <Box
            sx={{
                position: 'relative',
                flex: { xs: 'none', md: '0 0 50%' },
                width: { xs: '100%', md: '50%' },
                minHeight: { xs: 192, sm: 220, md: '100vh' },
                backgroundImage: `${overlayGradient}, url(${HERO_IMAGE})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                p: { xs: 3, md: 5 },
                color: 'common.white',
            }}
        >
            <Typography
                variant={isMdUp ? 'h3' : 'h5'}
                sx={{
                    fontWeight: 800,
                    lineHeight: 1.15,
                    letterSpacing: '-0.02em',
                    textShadow: '0 2px 24px rgba(0,0,0,0.35)',
                    maxWidth: 440,
                }}
            >
                Painel do anunciante
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    mt: 2,
                    maxWidth: 400,
                    opacity: 0.94,
                    lineHeight: 1.65,
                    fontWeight: 500,
                    textShadow: '0 1px 12px rgba(0,0,0,0.25)',
                }}
            >
                Acesse com CNPJ, e-mail e senha para gerenciar ofertas e validações de vouchers.
            </Typography>
        </Box>
    );

    return (
        <Stack direction={{ xs: 'column', md: 'row' }} minHeight={{ md: '100vh' }} sx={{ bgcolor: '#fafafa' }}>
            {heroPanel}

            <Box
                sx={{
                    flex: { xs: '1 1 auto', md: '0 0 50%' },
                    width: { xs: '100%', md: '50%' },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    py: { xs: 4, md: 5 },
                    px: { xs: 2.5, sm: 5, md: 6 },
                }}
            >
                <Stack spacing={3} sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
                    <Box component="img" src={Logo} alt="Eurobem" sx={{ width: 136, alignSelf: 'flex-start' }} />

                    <Stack spacing={0.75}>
                        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em', color: 'grey.900' }}>
                            Login do anunciante
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'grey.600', lineHeight: 1.55 }}>
                            Informe seus dados corporativos e credenciais de acesso.
                        </Typography>
                    </Stack>

                    <Card
                        elevation={0}
                        variant="outlined"
                        component="form"
                        onSubmit={handleSubmit}
                        noValidate
                    >
                        <CardHeader
                            sx={{
                                pb: 0,
                                pt: 2.5,
                                px: 2.5,
                                '& .MuiCardHeader-title': {
                                    fontSize: '0.9375rem',
                                    fontWeight: 600,
                                    color: 'grey.900',
                                },
                                '& .MuiCardHeader-subheader': {
                                    mt: 0.5,
                                    fontSize: '0.8125rem',
                                    lineHeight: 1.45,
                                    color: 'grey.600',
                                },
                            }}
                            title="Entrar na conta"
                            subheader="CNPJ e senha; e-mail se sua conta foi criada pelo cadastro público."
                        />
                        <CardContent sx={{ pt: 2, px: 2.5, pb: 2.5 }}>
                            <Stack spacing={2}>
                                <TextField
                                    required
                                    fullWidth
                                    name="cnpj"
                                    label="CNPJ"
                                    value={values.cnpj}
                                    onChange={e => setFieldValue('cnpj', e.target?.value || e)}
                                    InputProps={{ inputComponent: DocumentInputMasked }}
                                    autoComplete="organization"
                                />
                                <TextField
                                    fullWidth
                                    name="email"
                                    type="email"
                                    label="E-mail"
                                    autoComplete="email"
                                    value={values.email}
                                    onChange={handleChange}
                                    helperText="Obrigatório se você se cadastrou pelo site (login com e-mail). Conta antiga só com CNPJ pode deixar em branco."
                                />
                                <TextField
                                    required
                                    fullWidth
                                    name="password"
                                    type={values.showPassword ? 'text' : 'password'}
                                    label="Senha"
                                    autoComplete="current-password"
                                    value={values.password}
                                    onChange={handleChange}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setFieldValue('showPassword', !values.showPassword)}
                                                    edge="end"
                                                    aria-label="Alternar visibilidade da senha"
                                                >
                                                    {values.showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Button type="submit" variant="contained" size="large" fullWidth disabled={isSubmitting} sx={{ mt: 1, py: 1.25 }}>
                                    Entrar
                                </Button>
                                <Stack direction="column" spacing={1} sx={{ pt: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: 'grey.500', textAlign: 'center', display: 'block' }}>
                                        <Link to="/recuperar-senha?profile=advertiser" style={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                                            Esqueci minha senha
                                        </Link>
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'grey.500', textAlign: 'center', display: 'block' }}>
                                        Ainda não tem conta?{' '}
                                        <Link to="/anunciante/registro-anunciante" style={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                                            Criar cadastro de anunciante
                                        </Link>
                                    </Typography>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Typography variant="caption" sx={{ color: 'grey.500', textAlign: 'center', display: 'block' }}>
                        <Link to="/" style={{ color: 'grey.600', fontWeight: 500 }}>
                            Voltar para seleção de perfil
                        </Link>
                    </Typography>
                </Stack>
            </Box>
        </Stack>
    );
}
